"use strict";
// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
Object.defineProperty(exports, "__esModule", { value: true });
exports.createServerRunner = void 0;
const utils_1 = require("aws-amplify/utils");
const utils_2 = require("./utils");
const createTokenValidator_1 = require("./utils/createTokenValidator");
const auth_1 = require("./auth");
const utils_3 = require("./auth/utils");
/**
 * Creates the `runWithAmplifyServerContext` function to run Amplify server side APIs in an isolated request context.
 *
 * @remarks
 * This function should be called only once; you can use the returned `runWithAmplifyServerContext` across
 * your codebase.
 *
 * @param input The input used to create the `runWithAmplifyServerContext` function.
 * @param input.config The {@link ResourcesConfig} imported from the `amplifyconfiguration.json` file or manually
 * created.
 * @returns An object that contains the `runWithAmplifyServerContext` function.
 *
 * @example
 * import { createServerRunner } from '@aws-amplify/adapter-nextjs';
 * import config from './amplifyconfiguration.json';
 *
 * export const { runWithAmplifyServerContext } = createServerRunner({ config })
 */
const createServerRunner = ({ config, runtimeOptions, }) => {
    const amplifyConfig = (0, utils_1.parseAmplifyConfig)(config);
    const amplifyAppOrigin = process.env.AMPLIFY_APP_ORIGIN;
    utils_2.globalSettings.setRuntimeOptions(runtimeOptions ?? {});
    if ((0, utils_3.isValidOrigin)(amplifyAppOrigin)) {
        utils_2.globalSettings.setIsSSLOrigin((0, utils_3.isSSLOrigin)(amplifyAppOrigin));
        // update the isServerSideAuthEnabled flag of the globalSettings to true
        utils_2.globalSettings.enableServerSideAuth();
    }
    let tokenValidator;
    if (amplifyConfig?.Auth) {
        const { Cognito } = amplifyConfig.Auth;
        tokenValidator = (0, createTokenValidator_1.createTokenValidator)({
            userPoolId: Cognito?.userPoolId,
            userPoolClientId: Cognito?.userPoolClientId,
        });
    }
    const runWithAmplifyServerContext = (0, utils_2.createRunWithAmplifyServerContext)({
        config: amplifyConfig,
        tokenValidator,
        globalSettings: utils_2.globalSettings,
    });
    return {
        runWithAmplifyServerContext,
        createAuthRouteHandlers: (0, auth_1.createAuthRouteHandlersFactory)({
            config: amplifyConfig,
            amplifyAppOrigin,
            globalSettings: utils_2.globalSettings,
            runWithAmplifyServerContext,
        }),
    };
};
exports.createServerRunner = createServerRunner;
